import { Controller } from "@hotwired/stimulus"
import * as offlineQueue from "../lib/offline_queue"

// A replayed capture's followed response is only trustworthy as "synced" if
// it actually landed back on a run-mode stage page — see replayItem's
// class-level guard below.
const RUN_STAGE_PATH_PATTERN = /^\/weighing_sessions\/\d+\/run$/

// Module-scoped, not an instance flag (final review I2): Turbo body swaps
// (e.g. a run-mode stage navigation while a replay pass is mid-flight)
// disconnect the old controller instance and connect a fresh one, but the
// old instance's in-flight async replayQueue() keeps running to
// completion regardless — it isn't cancelled by disconnect(). A `this.
// replaying` instance flag can't see across that boundary, so the new
// instance's own maybeReplay() (fired from connect()) could start a SECOND
// concurrent pass over the same IndexedDB queue, both fetch()ing the same
// still-present item before either's success removes it — double-
// submitting a capture. One lock shared by every instance closes that.
let replayLock = false

// Run-mode offline support: service worker registration, a connectivity
// banner, stage prefetch (Task 3), and the capture queue + replay (Task 4:
// intercepting stage/photo form submits while offline, serializing them
// into offline_queue.js's IndexedDB store, and replaying them in order once
// connectivity returns). Attached once, on the run-mode layout's <body>
// (app/views/layouts/run_mode.html.slim); form submit interception is wired
// per-form via data-action="submit->offline#interceptSubmit" (see
// app/views/weighing_sessions/run_stages/_reading, _attitude,
// _check_item .html.slim).
export default class extends Controller {
  static targets = [
    "banner",
    "bannerText",
    "bannerLink",
    "errorList",
    "offlineTemplate",
    "queuedTemplate",
    "errorTemplate",
    "signedOutTemplate"
  ]
  static values = {
    swUrl: { type: String, default: "/service-worker" },
    scope: { type: String, default: "/" },
    // Every run-stage URL for the current session (see
    // data-offline-stage-urls-value in run_mode.html.slim, sourced from
    // Weighing::RunFlow#stage_keys). Warmed into the SW cache below.
    stageUrls: Array
  }

  connect() {
    this.registerServiceWorker()
    this.prefetchStages()

    // Bound once so add/removeEventListener refer to the same function.
    this.handleConnectivityChange = () => {
      this.updateBanner()
      this.maybeReplay()
    }
    window.addEventListener("online", this.handleConnectivityChange)
    window.addEventListener("offline", this.handleConnectivityChange)

    // Backstop for the `online` event: flaky hangar Wi-Fi doesn't always
    // dip to a hard "offline" the browser notices. No-ops when the queue
    // is empty — see maybeReplay.
    this.replayTimer = window.setInterval(() => this.maybeReplay(), 30000)

    this.updateBanner()
    this.maybeReplay()
  }

  disconnect() {
    window.removeEventListener("online", this.handleConnectivityChange)
    window.removeEventListener("offline", this.handleConnectivityChange)
    window.clearInterval(this.replayTimer)

    if (this.handleControllerChange && "serviceWorker" in navigator) {
      navigator.serviceWorker.removeEventListener("controllerchange", this.handleControllerChange)
    }
  }

  registerServiceWorker() {
    // Unsupported browsers just get the online-only experience —
    // allow_browser already gates truly ancient ones.
    if (!("serviceWorker" in navigator)) return
    navigator.serviceWorker.register(this.swUrlValue, { scope: this.scopeValue }).catch((error) => {
      // eslint-disable-next-line no-console
      console.warn("[offline] service worker registration failed", error)
    })
  }

  // Warms the SW's cache with every stage URL for this session (Task 3) so
  // the whole guided flow is reachable offline. Only worth doing while
  // online; a mid-session reconnect relies on the fetch handler's own
  // cache-fallback rather than a fresh prefetch.
  prefetchStages() {
    if (!("serviceWorker" in navigator)) return
    if (!navigator.onLine) return
    if (!this.hasStageUrlsValue || this.stageUrlsValue.length === 0) return

    if (navigator.serviceWorker.controller) {
      this.postPrefetch()
      return
    }

    // First-load case: cover both signals that can follow registration —
    // `ready` (an active worker exists) and `controllerchange` (this page
    // starts being controlled, via the SW's own clients.claim()). Posting
    // from both is harmless — PREFETCH just re-adds the same URLs.
    navigator.serviceWorker.ready.then(() => this.postPrefetch())

    this.handleControllerChange = () => this.postPrefetch()
    navigator.serviceWorker.addEventListener("controllerchange", this.handleControllerChange)
  }

  postPrefetch() {
    if (!navigator.serviceWorker.controller) return

    navigator.serviceWorker.controller.postMessage({ type: "PREFETCH", urls: this.stageUrlsValue })
  }

  // Intercepts a stage/photo form submit ONLY while offline — the online
  // path is untouched, including Turbo's normal handling and the server's
  // full optimistic-locking check. Serializes the form into an
  // offline_queue.js record and queues it instead of letting the browser
  // attempt (and fail) a real network request.
  async interceptSubmit(event) {
    if (navigator.onLine) return
    event.preventDefault()

    const form = event.target
    const methodOverride = form.querySelector('input[name="_method"]')?.value
    const method = (methodOverride || form.getAttribute("method") || "get").toUpperCase()
    const stageKey = form.querySelector('input[name="run_stage"]')?.value || null
    const csrf = document.querySelector('meta[name="csrf-token"]')?.content || null

    // event.preventDefault() already ran, so a failure here (quota
    // exceeded, private-browsing storage restrictions, any other
    // IndexedDB error) would otherwise lose this capture with NO trace at
    // all — the browser's own submit never happens either (final review
    // M3). The form's own fields are untouched either way (nothing clears
    // them until a successful enqueue), so the operator can simply retry
    // the same Save & continue tap once told to.
    try {
      await offlineQueue.enqueue({
        url: form.getAttribute("action") || form.action,
        method,
        formEntries: serializeFormData(new FormData(form)),
        csrf,
        stageKey
      })
    } catch (error) {
      // eslint-disable-next-line no-console
      console.warn("[offline] failed to queue capture locally", error)
      await this.showEnqueueFailedBanner()
      return
    }

    await this.updateBanner()
  }

  // Called on the `online` event and every 30s tick. Re-entrancy-safe: the
  // timer and the online event can both fire around the same moment — and,
  // via the module-scoped replayLock (see above), safe across a Turbo body
  // swap replacing this controller instance mid-pass too.
  async maybeReplay() {
    if (replayLock || !navigator.onLine) return
    if ((await offlineQueue.count()) === 0) return

    replayLock = true
    try {
      await this.replayQueue()
    } finally {
      replayLock = false
    }
  }

  // Strictly sequential, in enqueue order (offlineQueue.all() is ascending
  // by id). Delegates each item to replayItem — see there for the
  // success/signed-out/error/network-error split. A signed-out result
  // stops the WHOLE pass immediately (queue order stays intact — nothing
  // after it has been attempted) rather than hammering every remaining
  // item with the same expired session. Reloads the page once anything
  // actually synced, to reflect server-side state freshly.
  async replayQueue() {
    const items = await offlineQueue.all()
    let replayedAny = false

    for (const item of items) {
      if (!navigator.onLine) break

      const outcome = await this.replayItem(item)
      if (outcome === "network-error") break
      if (outcome === "signed-out") {
        await this.showSignedOutBanner()
        return
      }
      if (outcome === "success") replayedAny = true
    }

    if (replayedAny) {
      window.location.reload()
    } else {
      await this.updateBanner()
    }
  }

  // Replays exactly one queued item, used both by replayQueue's loop and
  // by a single "Retry now" click (see retryItem). Returns one of:
  // "success" (drained), "signed-out" (session expired mid-shift — see
  // below), "error" (4xx/5xx, OR a redirect-disguised rejection — see the
  // `sync` check and the run-mode-page guard below — flagged via
  // markError), "network-error" (fetch() itself threw — connectivity
  // dropped again; caller leaves the item untouched for a later attempt).
  //
  // redirect: "follow" (not "manual") is required to tell a real success
  // apart from a session-expiry redirect: Devise redirects an
  // unauthenticated HTML-format request to /users/sign_in with an
  // ordinary same-origin 302 rather than a 401 — see the spec's "CSRF on
  // replay" section. Following it lets us inspect where we actually
  // landed: a real success also redirects (back to the run page) and
  // resolves 200/response.ok; landing on /users/sign_in means the
  // capture was NEVER persisted — draining it here would silently
  // discard a real measurement, so it's treated as its own outcome
  // instead of falling into the generic "error" bucket (which retries
  // automatically forever against a session that keeps rejecting it).
  async replayItem(item) {
    // The CURRENT page's token, not the one stored with the item at capture
    // time (final review I1): a capture queued minutes or hours earlier —
    // possibly across a sign-out/sign-in cycle on a shared device — carries
    // a token that may no longer match the session replay actually runs
    // against, which used to make replay fail forever even after signing
    // back in and reopening run mode (a fresh page load mints a fresh
    // token). Falls back to the stored token only if no page is currently
    // loaded with one (defensive; every real caller of replayItem runs from
    // a rendered run-mode page, which always has the meta tag).
    const csrf = document.querySelector('meta[name="csrf-token"]')?.content || item.csrf

    let response
    try {
      response = await fetch(item.url, {
        method: item.method,
        body: rebuildFormData(item.formEntries),
        headers: csrf ? { "X-CSRF-Token": csrf } : {},
        redirect: "follow"
      })
    } catch {
      return "network-error"
    }

    if (response.redirected && new URL(response.url).pathname === "/users/sign_in") {
      return "signed-out"
    }

    // Class-level guard against ANY OTHER unexpected redirect destination —
    // not just sign-in. Every legitimate replay target (a stage PATCH, a
    // photo POST) redirects back to a /weighing_sessions/<id>/run page on
    // success; landing anywhere else after following is proof the capture
    // was NOT actually persisted, even though the response itself is a
    // followed, ok (200-ish) one and would otherwise look exactly like a
    // success. This is what generalizes Task 4's parked "locked/finalized
    // session" finding: a locked session fails WeighingSessionsController's
    // authorize(:update?), which is rescued by
    // ApplicationController#user_not_authorized via `redirect_back` — a
    // Pundit-standard pattern this app didn't write custom handling for and
    // that a replayed fetch() can trip. The same shape of bug — a followed
    // redirect landing somewhere other than the run page, an ok response
    // that isn't really a synced capture — could come from other causes
    // too (any future controller change that redirects a rejected/denied
    // request elsewhere), so the guard is written generically rather than
    // special-cased to the locked-session scenario.
    if (response.ok && !RUN_STAGE_PATH_PATTERN.test(new URL(response.url).pathname)) {
      await offlineQueue.markError(item.id, "Landed on an unexpected page after replay — not confirmed synced.")
      return "error"
    }

    if (response.ok) {
      // WeighingAttachmentsController redirects to the SAME run stage on
      // BOTH a saved photo and a rejected one (wrong file type, too
      // large — see redirect_target's `sync` param), so a followed
      // redirect landing on a 200 run page is NOT proof this particular
      // item persisted for a photo upload. `sync` is absent for every
      // other replayed item (stage PATCHes never carry it), so this only
      // ever fires for attachment replays.
      const sync = new URL(response.url).searchParams.get("sync")
      if (sync === "failed") {
        await offlineQueue.markError(item.id, "Rejected by the server (e.g. an invalid file) — not synced.")
        return "error"
      }

      await offlineQueue.remove(item.id)
      return "success"
    }

    await offlineQueue.markError(item.id, `Server responded ${response.status}`)
    return "error"
  }

  // "Retry now" on one flagged item in the error list (see renderErrorList
  // / buildErrorRow) — replays just that item rather than the whole queue.
  // Routed through the same module-scoped replayLock as maybeReplay (final
  // review I2): without it, a manual retry click could race an automatic
  // background pass that reaches the very same item, double-submitting it.
  // A no-op while locked (rather than queueing behind it) is deliberate —
  // the automatic pass this raced against will reach this same item itself
  // momentarily, so nothing is lost by the click doing nothing this time.
  async retryItem(event) {
    if (replayLock) return

    const item = (await offlineQueue.all()).find((record) => record.id === event.params.id)
    if (!item) return

    replayLock = true
    try {
      const outcome = await this.replayItem(item)
      if (outcome === "signed-out") {
        await this.showSignedOutBanner()
      } else if (outcome === "success") {
        window.location.reload()
      } else {
        await this.updateBanner()
      }
    } finally {
      replayLock = false
    }
  }

  // "Discard" on one flagged item — the deliberate, operator-driven way
  // out for a capture that will never replay successfully (e.g. the
  // operator already re-entered the correct value through the normal
  // online path for the same stage). Never silent: confirm() names the
  // stage before removing anything.
  async discardItem(event) {
    const { id, stageKey } = event.params
    const proceed = window.confirm(
      `Discard the queued capture for ${stageKey || "this stage"}? It will NOT be synced.`
    )
    if (!proceed) return

    await offlineQueue.remove(id)
    await this.updateBanner()
  }

  async updateBanner() {
    if (!this.hasBannerTarget) return

    const items = await offlineQueue.all()
    const errored = items.filter((item) => item.error)

    if (items.length === 0 && navigator.onLine) {
      this.bannerTarget.hidden = true
      this.hideErrorList()
      return
    }

    this.bannerTarget.hidden = false

    if (items.length === 0) {
      this.renderBanner(this.offlineTemplateTarget.content.textContent.trim())
      this.hideErrorList()
      return
    }

    if (errored.length > 0) {
      const text = this.errorTemplateTarget.content.textContent
        .trim()
        .replace("%COUNT%", items.length)
        .replace("%ERROR_COUNT%", errored.length)
      // Links to the OLDEST errored capture's stage — replay processes the
      // queue in that same order, so it's the next one the operator needs
      // to resolve.
      this.renderBanner(text, this.stageUrl(errored[0].stageKey))
      this.renderErrorList(errored)
      return
    }

    const text = this.queuedTemplateTarget.content.textContent.trim().replace("%COUNT%", items.length)
    this.renderBanner(text)
    this.hideErrorList()
  }

  // Session expired mid-shift (see replayItem's "signed-out" outcome):
  // nothing was drained, so re-read the actual current count rather than
  // trust the caller's — an earlier item in the same pass may have
  // already synced before this one hit the redirect.
  async showSignedOutBanner() {
    const count = await offlineQueue.count()
    this.bannerTarget.hidden = false
    const text = this.signedOutTemplateTarget.content.textContent.trim().replace("%COUNT%", count)
    this.renderBanner(text)
    this.hideErrorList()
  }

  // interceptSubmit's enqueue() itself failed (final review M3) — there is
  // no queue record for this capture at all, so the normal error state
  // above (errorTemplateTarget's %COUNT%/%ERROR_COUNT% counting real
  // flagged records) doesn't apply; this reuses the same banner mechanism
  // (bannerTarget/renderBanner) with its own message instead. hasBannerTarget
  // guarded the same way updateBanner is, in case a form somehow submits
  // before connect() has finished wiring targets.
  async showEnqueueFailedBanner() {
    if (!this.hasBannerTarget) return

    this.bannerTarget.hidden = false
    this.renderBanner("Couldn't save this capture locally — try again before leaving this stage.")
    this.hideErrorList()
  }

  renderBanner(text, linkHref) {
    this.bannerTextTarget.textContent = text
    this.bannerLinkTarget.hidden = !linkHref
    if (linkHref) this.bannerLinkTarget.href = linkHref
  }

  // One row per flagged item, each with its own Retry/Discard — so a
  // permanently-invalid capture has a deliberate operator way out instead
  // of sitting forever behind the single aggregate banner link. Built with
  // plain DOM calls + data-action (not innerHTML) so Stimulus wires the
  // buttons up the same way it would any other action, including on
  // elements added long after connect().
  renderErrorList(errored) {
    if (!this.hasErrorListTarget) return
    this.errorListTarget.replaceChildren(...errored.map((item) => this.buildErrorRow(item)))
    this.errorListTarget.hidden = false
  }

  hideErrorList() {
    if (!this.hasErrorListTarget) return
    this.errorListTarget.hidden = true
    this.errorListTarget.replaceChildren()
  }

  buildErrorRow(item) {
    const row = document.createElement("li")
    row.className = "run-mode__offline-error-row"

    const label = document.createElement("span")
    label.textContent = `${item.stageKey || "Unknown stage"} — ${item.error || "failed to sync"}`
    row.append(label)

    const retry = document.createElement("button")
    retry.type = "button"
    retry.className = "btn btn-sm btn-outline-secondary"
    retry.textContent = "Retry now"
    retry.dataset.action = "click->offline#retryItem"
    retry.dataset.offlineIdParam = item.id
    row.append(retry)

    const discard = document.createElement("button")
    discard.type = "button"
    discard.className = "btn btn-sm btn-outline-danger"
    discard.textContent = "Discard"
    discard.dataset.action = "click->offline#discardItem"
    discard.dataset.offlineIdParam = item.id
    discard.dataset.offlineStageKeyParam = item.stageKey || ""
    row.append(discard)

    return row
  }

  // The run-mode URL for a stage key, built from the CURRENT page's own
  // path (every run stage lives at /weighing_sessions/:id/run, keyed off a
  // ?stage= param) — simpler than aligning stageUrlsValue's unkeyed array.
  stageUrl(stageKey) {
    return `${window.location.pathname}?stage=${encodeURIComponent(stageKey)}`
  }
}

// FormData -> offline_queue.js's formEntries shape. A File becomes
// {blob, filename, type}: Blob storage round-trips through IndexedDB
// natively, but a bare Blob has no name, so the filename travels alongside
// it for rebuildFormData to reconstruct a proper multipart file part.
//
// weighing_session[lock_version] is dropped here — ACCEPTED RISK, see
// docs/superpowers/specs/2026-08-06-offline-run-mode-design.md, "The
// lock_version tradeoff": a queued capture's lock_version is stale by the
// time it replays (possibly minutes later, possibly after earlier queued
// items in the SAME session already bumped it), so submitting it would
// just trigger a false-positive StaleObjectError. ONLY the offline queue
// path is affected — interceptSubmit only runs when !navigator.onLine, so
// the live online submit keeps full optimistic locking, untouched.
function serializeFormData(formData) {
  const entries = []
  for (const [name, value] of formData.entries()) {
    if (name === "weighing_session[lock_version]") continue

    if (value instanceof File) {
      entries.push([name, { blob: value, filename: value.name, type: value.type }])
    } else {
      entries.push([name, value])
    }
  }
  return entries
}

// The inverse of serializeFormData, for replay: rebuilds a real FormData
// (and, inside it, real File-shaped parts) from a stored record's
// formEntries.
function rebuildFormData(formEntries) {
  const formData = new FormData()
  formEntries.forEach(([name, value]) => {
    if (value && typeof value === "object" && "blob" in value) {
      formData.append(name, value.blob, value.filename)
    } else {
      formData.append(name, value)
    }
  })
  return formData
}
